import React, { useCallback, useMemo, useState } from 'react';
import { UpdateDecisionsView } from 'api';
import { useStore } from 'common/store';
import { AlertAction } from 'components/Alert/type';
import FirmsService from 'services/firms';
import { useResponse } from 'services/hooks/useResponse';
import { Logger } from 'utilities';
import DecisionAlert from './components/DecisionAlert';
import { DecisionData } from './components/DecisionAlert/DecisionAlert.props';
import DisconnectApproachDialog from './components/DisconnectApproachDialog';

type DecisionDataResponse = {
  results: UpdateDecisionsView[];
};

const usePendingDecisionsByFirm = () => {
  const [decisions, setDecisions] = useState<UpdateDecisionsView[] | undefined>();
  const [decisionData, setDecisionData] = useState<DecisionData | undefined>();
  const [isFetchingDecisions, setIsFetchingDecisions] = useState(false);
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);

  const [{ firmId }] = useStore();
  const { successNotification, errorNotification } = useResponse();

  const firmService = useMemo(() => new FirmsService(), []);

  const fetchPendingDecisions = useCallback(
    async firmIdParam => {
      try {
        setIsFetchingDecisions(true);
        const response = (await firmService.getPendingDecisionsByFirm(firmIdParam)) as DecisionDataResponse;
        if (response) {
          setDecisions(response.results);
        }
        setIsFetchingDecisions(false);
      } catch (error) {
        Logger.error('Error fetching pending decisions', error);
        setDecisions(undefined);
        errorNotification('An error occurred while retrieving the pending decisions for this firm.');
        setIsFetchingDecisions(false);
      }
    },
    [errorNotification, firmService]
  );

  const updateData = useCallback(
    async (firmIdParam, decisionDataParam) => {
      try {
        const response = (await firmService.updatePendingDecisionsByFirm(
          firmIdParam,
          decisionDataParam
        )) as DecisionDataResponse;
        if (response) {
          setDecisions(response.results);
          successNotification('Valuations using global comp group were updated.');
        }
      } catch (error) {
        Logger.error('Error updating pending decisions', error);
        errorNotification('An error occurred while updating the pending decisions for this firm.');
      }
    },
    [errorNotification, firmService, successNotification]
  );

  const displayDecisionAlerts = useCallback(
    () =>
      decisions
      && decisions.length > 0 && (
        <>
          {decisions?.map(decision => (
            <DecisionAlert
              key={decision.id}
              cause={decision as unknown as UpdateDecisionsView}
              updateDecisionService={updateData}
              setDecisionData={setDecisionData}
              setShowDisconnectDialog={setShowDisconnectDialog}
            />
          ))}
        </>
      ),
    [decisions, updateData]
  );

  const confirmDisconnect = useCallback(() => {
    updateData(firmId, decisionData);
    setShowDisconnectDialog(false);
  }, [decisionData, firmId, updateData]);

  const disconnectDialogActions: AlertAction[] = useMemo(
    () => [
      {
        label: 'Cancel',
        callback: () => setShowDisconnectDialog(false),
      },
      {
        label: 'Disconnect',
        callback: confirmDisconnect,
        variant: 'contained',
        type: 'danger',
      },
    ],
    [confirmDisconnect]
  );

  const onCloseDisconnectDialog = () => {
    setShowDisconnectDialog(false);
    setDecisionData(undefined);
  };

  const getDisconnectDialog = useCallback(
    () =>
      showDisconnectDialog && (
        <DisconnectApproachDialog
          open={showDisconnectDialog}
          onClose={onCloseDisconnectDialog}
          actions={disconnectDialogActions as DialogAction[]}
        />
      ),
    [disconnectDialogActions, showDisconnectDialog]
  );

  return {
    decisions,
    displayDecisionAlerts,
    fetchPendingDecisions,
    getDisconnectDialog,
    isFetchingDecisions,
    updateData,
  };
};

export default usePendingDecisionsByFirm;
